// src/components/DownloadExcel.jsx
import * as XLSX from "xlsx";
import { forwardRef, useImperativeHandle } from "react";

const DownloadExcel = forwardRef(({
  kpiData,
  stockData,
  forecastTertiaryData,
  driversData,
  exposureData,
  inputsData,
  pastActualsMonthlyAggregation,
  pastForecastMonthlyAggregation,
  projectedMonthlyAggregation,
  fileName = "forecast.xlsx",
  countPastActuals,
  countPastForecast,
  countProjected
}, ref) => {

  const handleDownload = (selectedItemName, weeklyRanges) => {
    const headerData = [
      [selectedItemName, 1],
      ["Last Yr. Actuals", 12],
      ["Past Actuals", countPastActuals],
      ["Current Week", countPastForecast],
      ["Projected", countProjected]
    ]

    // 1) Calculate total columns
    const totalCols = headerData.reduce((sum, [, span = 1]) => sum + span, 0);

    // 2) Create one row with all columns (empty)
    const headerRow = Array(totalCols).fill("");

    // 3) Put the text in the FIRST column of each merged region
    let colStart = 0;
    headerData.forEach(([title, span = 1]) => {
      headerRow[colStart] = title;
      colStart += span;
    });

    let wkHeaderRow = [
      'KPI',
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'July',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    let wkHeader = [];
    for (const weeklyRangesKey in weeklyRanges) {
      let wkData = weeklyRanges[weeklyRangesKey]['start'].split("-");
      let wkMonth = wkData[1];
      let wkStart = wkData[0];
      let wkEnd = weeklyRanges[weeklyRangesKey]['end'].split("-")[0];

      let cellData = 'W' + weeklyRangesKey + ' - ' + wkMonth
      cellData += ' (' + wkStart + ' - ' + wkEnd + ')';

      wkHeader.push(cellData);
    }

    //  Tertiary Vol
    let terVolRowHeader = [kpiData.props[0]['rowData']];
    let terVolRowData = [...kpiData.props[0]['monthlyValues'], ...kpiData.props[0]['weeklyValues'], ...kpiData.props[0]['forecastValues'], ...kpiData.props[0]['projectionValues']];
    let terVolRow = [...terVolRowHeader, ...terVolRowData];
    let terVolRowTotal = [...pastActualsMonthlyAggregation['kpis'][0]['val'], ...pastForecastMonthlyAggregation['kpis'][0]['val'], ...projectedMonthlyAggregation['kpis'][0]['val']];

    //  Secondary Vol
    let secVolRowHeader = [kpiData.props[1]['rowData']];
    let secVolRowData = [...kpiData.props[1]['monthlyValues'], ...kpiData.props[1]['weeklyValues'], ...kpiData.props[1]['forecastValues'], ...kpiData.props[1]['projectionValues']];
    let secVolRow = [...secVolRowHeader, ...secVolRowData];
    let secVolRowTotal = [...pastActualsMonthlyAggregation['kpis'][1]['val'], ...pastForecastMonthlyAggregation['kpis'][1]['val'], ...projectedMonthlyAggregation['kpis'][1]['val']];

    //  Primary Vol
    let primVolRowHeader = [kpiData.props[2]['rowData']];
    let primVolRowData = [...kpiData.props[2]['monthlyValues'], ...kpiData.props[2]['weeklyValues'], ...kpiData.props[2]['forecastValues'], ...kpiData.props[2]['projectionValues']];
    let primVolRow = [...primVolRowHeader, ...primVolRowData];
    let primVolRowTotal = [...pastActualsMonthlyAggregation['kpis'][2]['val'], ...pastForecastMonthlyAggregation['kpis'][2]['val'], ...projectedMonthlyAggregation['kpis'][2]['val']];

    // Weeks
    let wkIndex = 0;
    let wkHeaderLength = wkHeader.length;

    let prevMonth = 'Jan';
    let totalMonthCount = 0;
    let totalKeyArr = [];

    for (let index = wkHeaderRow.length; index <= terVolRowData.length; index++) {
      let wkData = wkHeader[wkIndex].split("-");
      let wkMonthData = wkData[1].split("(")
      let wkMonth = wkMonthData[0].trim();

      if (prevMonth !== wkMonth) {
        wkHeaderRow.push(prevMonth);
        prevMonth = wkMonth;
        totalMonthCount = totalMonthCount + 1;
        totalKeyArr.push(index);
      }

      wkHeaderRow.push(wkHeader[wkIndex]);

      wkIndex++;
      if (wkIndex >= wkHeaderLength) {
        wkIndex = 0;
      }
    }

    // Stock
    // let stockRow = [];

    // Stock - Opening DBR Stock
    let openingDBRStockRowHeader = [stockData.props[0]['rowData']];
    let openingDBRStockRowData = [...stockData.props[0]['monthlyValues'], ...stockData.props[0]['weeklyValues'], ...stockData.props[0]['forecastValues'], ...stockData.props[0]['projectionValues']];
    let openingDBRStockRow = [...openingDBRStockRowHeader, ...openingDBRStockRowData];
    let openingDBRStockRowTotal = [...pastActualsMonthlyAggregation['stocks'][0]['val'], ...pastForecastMonthlyAggregation['stocks'][0]['val'], ...projectedMonthlyAggregation['stocks'][0]['val']];

    // Stock - Opening Retailer Stock
    let openingRetailerStockRowHeader = [stockData.props[1]['rowData']];
    let openingRetailerStockRowData = [...stockData.props[1]['monthlyValues'], ...stockData.props[1]['weeklyValues'], ...stockData.props[1]['forecastValues'], ...stockData.props[1]['projectionValues']];
    let openingRetailerStockRow = [...openingRetailerStockRowHeader, ...openingRetailerStockRowData];
    let openingRetailerStockRowTotal = [...pastActualsMonthlyAggregation['stocks'][1]['val'], ...pastForecastMonthlyAggregation['stocks'][1]['val'], ...projectedMonthlyAggregation['stocks'][1]['val']];

    // Stock - Opening Market Stock
    let openingMarketStockRowHeader = [stockData.props[2]['rowData']];
    let openingMarketStockRowData = [...stockData.props[2]['monthlyValues'], ...stockData.props[2]['weeklyValues'], ...stockData.props[2]['forecastValues'], ...stockData.props[2]['projectionValues']];
    let openingMarketStockRow = [...openingMarketStockRowHeader, ...openingMarketStockRowData];
    let openingMarketStockRowTotal = [...pastActualsMonthlyAggregation['stocks'][2]['val'], ...pastForecastMonthlyAggregation['stocks'][2]['val'], ...projectedMonthlyAggregation['stocks'][2]['val']];

    // Stock - Closing DBR Stock
    let closingDBRStockRowHeader = [stockData.props[3]['rowData']];
    let closingDBRStockRowData = [...stockData.props[3]['monthlyValues'], ...stockData.props[3]['weeklyValues'], ...stockData.props[3]['forecastValues'], ...stockData.props[3]['projectionValues']];
    let closingDBRStockRow = [...closingDBRStockRowHeader, ...closingDBRStockRowData];
    let closingDBRStockRowTotal = [...pastActualsMonthlyAggregation['stocks'][3]['val'], ...pastForecastMonthlyAggregation['stocks'][3]['val'], ...projectedMonthlyAggregation['stocks'][3]['val']];

    // Stock - Closing Retailer Stock
    let closingRetailerStockRowHeader = [stockData.props[4]['rowData']];
    let closingRetailerStockRowData = [...stockData.props[4]['monthlyValues'], ...stockData.props[4]['weeklyValues'], ...stockData.props[4]['forecastValues'], ...stockData.props[4]['projectionValues']];
    let closingRetailerStockRow = [...closingRetailerStockRowHeader, ...closingRetailerStockRowData];
    let closingRetailerStockRowTotal = [...pastActualsMonthlyAggregation['stocks'][4]['val'], ...pastForecastMonthlyAggregation['stocks'][4]['val'], ...projectedMonthlyAggregation['stocks'][4]['val']];

    // Stock - Market Stock
    let closingMarketStockRowHeader = [stockData.props[5]['rowData']];
    let closingMarketStockRowData = [...stockData.props[5]['monthlyValues'], ...stockData.props[5]['weeklyValues'], ...stockData.props[5]['forecastValues'], ...stockData.props[5]['projectionValues']];
    let closingMarketStockRow = [...closingMarketStockRowHeader, ...closingMarketStockRowData];
    let closingMarketStockRowTotal = [...pastActualsMonthlyAggregation['stocks'][5]['val'], ...pastForecastMonthlyAggregation['stocks'][5]['val'], ...projectedMonthlyAggregation['stocks'][5]['val']];

    // Stock - Market DOS Stock
    let marketDOSRowHeader = [stockData.props[6]['rowData']];
    let marketDOSRowData = [...stockData.props[6]['monthlyValues'], ...stockData.props[6]['weeklyValues'], ...stockData.props[6]['forecastValues'], ...stockData.props[6]['projectionValues']];
    let marketDOSRow = [...marketDOSRowHeader, ...marketDOSRowData];
    let marketDOSRowTotal = [...pastActualsMonthlyAggregation['stocks'][6]['val'], ...pastForecastMonthlyAggregation['stocks'][6]['val'], ...projectedMonthlyAggregation['stocks'][6]['val']];

    // stockRow = [openingDBRStockRow, openingRetailerStockRow, openingMarketStockRow, closingDBRStockRow, closingRetailerStockRow, closingMarketStockRow, marketDOSRow];

    // Forecast Tertiary
    // let forecastTertiaryRow = [];

    // Forecast Tertiary - Parameters
    let parameterForecastTertiaryRowHeader = [forecastTertiaryData.props[0]['rowData']];
    let parameterForecastTertiaryRowData = [...forecastTertiaryData.props[0]['monthlyValues'], ...forecastTertiaryData.props[0]['weeklyValues'], ...forecastTertiaryData.props[0]['forecastValues'], ...forecastTertiaryData.props[0]['projectionValues']];
    let parameterForecastTertiaryRow = [...parameterForecastTertiaryRowHeader, ...parameterForecastTertiaryRowData];
    let parameterForecastTertiaryRowTotal = [...pastActualsMonthlyAggregation['forecast_tertiary'][0]['val'], ...pastForecastMonthlyAggregation['forecast_tertiary'][0]['val'], ...projectedMonthlyAggregation['forecast_tertiary'][0]['val']];

    // Forecast Tertiary - Pilot
    let pilotForecastTertiaryRowHeader = [forecastTertiaryData.props[1]['rowData']];
    let pilotForecastTertiaryRowData = [...forecastTertiaryData.props[1]['monthlyValues'], ...forecastTertiaryData.props[1]['weeklyValues'], ...forecastTertiaryData.props[1]['forecastValues'], ...forecastTertiaryData.props[1]['projectionValues']];
    let pilotForecastTertiaryRow = [...pilotForecastTertiaryRowHeader, ...pilotForecastTertiaryRowData];
    let pilotForecastTertiaryRowTotal = [...pastActualsMonthlyAggregation['forecast_tertiary'][1]['val'], ...pastForecastMonthlyAggregation['forecast_tertiary'][1]['val'], ...projectedMonthlyAggregation['forecast_tertiary'][1]['val']];

    // Forecast Tertiary - National DRR
    let nationalDrrForecastTertiaryRowHeader = [forecastTertiaryData.props[2]['rowData']];
    let nationalDrrForecastTertiaryRowData = [...forecastTertiaryData.props[2]['monthlyValues'], ...forecastTertiaryData.props[2]['weeklyValues'], ...forecastTertiaryData.props[2]['forecastValues'], ...forecastTertiaryData.props[2]['projectionValues']];
    let nationalDrrForecastTertiaryRow = [...nationalDrrForecastTertiaryRowHeader, ...nationalDrrForecastTertiaryRowData];
    let nationalDrrForecastTertiaryRowTotal = [...pastActualsMonthlyAggregation['forecast_tertiary'][2]['val'], ...pastForecastMonthlyAggregation['forecast_tertiary'][2]['val'], ...projectedMonthlyAggregation['forecast_tertiary'][2]['val']];

    // Forecast Tertiary - Activating Outlet
    let aoForecastTertiaryRowHeader = [forecastTertiaryData.props[3]['rowData']];
    let aoForecastTertiaryRowData = [...forecastTertiaryData.props[3]['monthlyValues'], ...forecastTertiaryData.props[3]['weeklyValues'], ...forecastTertiaryData.props[3]['forecastValues'], ...forecastTertiaryData.props[3]['projectionValues']];
    let aoForecastTertiaryRow = [...aoForecastTertiaryRowHeader, ...aoForecastTertiaryRowData];
    let aoForecastTertiaryRowTotal = [...pastActualsMonthlyAggregation['forecast_tertiary'][3]['val'], ...pastForecastMonthlyAggregation['forecast_tertiary'][3]['val'], ...projectedMonthlyAggregation['forecast_tertiary'][3]['val']];

    // forecastTertiaryRow = [parameterForecastTertiaryRow, pilotForecastTertiaryRow, nationalDrrForecastTertiaryRow, aoForecastTertiaryRow];

    // Drivers
    // let driversRow = [];

    // Drivers - Stock/Outlet
    let stockPerOutletDriversHeader = [driversData.props[0]['rowData']];
    let stockPerOutletDriversData = [...driversData.props[0]['monthlyValues'], ...driversData.props[0]['weeklyValues'], ...driversData.props[0]['forecastValues'], ...driversData.props[0]['projectionValues']];
    let stockPerOutletDriversRow = [...stockPerOutletDriversHeader, ...stockPerOutletDriversData];
    let stockPerOutletDriversRowTotal = [...pastActualsMonthlyAggregation['drivers'][0]['val'], ...pastForecastMonthlyAggregation['drivers'][0]['val'], ...projectedMonthlyAggregation['drivers'][0]['val']];

    // Drivers - AO/SO
    let aoPerSoDriversHeader = [driversData.props[1]['rowData']];
    let aoPerSoDriversData = [...driversData.props[1]['monthlyValues'], ...driversData.props[1]['weeklyValues'], ...driversData.props[1]['forecastValues'], ...driversData.props[1]['projectionValues']];
    let aoPerSoDriversRow = [...aoPerSoDriversHeader, ...aoPerSoDriversData];
    let aoPerSoDriversRowTotal = [...pastActualsMonthlyAggregation['drivers'][1]['val'], ...pastForecastMonthlyAggregation['drivers'][1]['val'], ...projectedMonthlyAggregation['drivers'][1]['val']];

    // Drivers - Conversion
    let thruputDriversHeader = [driversData.props[2]['rowData']];
    let thruputDriversData = [...driversData.props[2]['monthlyValues'], ...driversData.props[2]['weeklyValues'], ...driversData.props[2]['forecastValues'], ...driversData.props[2]['projectionValues']];
    let thruputDriversRow = [...thruputDriversHeader, ...thruputDriversData];
    let thruputDriversRowTotal = [...pastActualsMonthlyAggregation['drivers'][2]['val'], ...pastForecastMonthlyAggregation['drivers'][2]['val'], ...projectedMonthlyAggregation['drivers'][2]['val']];

    // Drivers - WOD/AO
    let wodPerAoDriversHeader = [driversData.props[3]['rowData']];
    let wodPerAoDriversData = [...driversData.props[3]['monthlyValues'], ...driversData.props[3]['weeklyValues'], ...driversData.props[3]['forecastValues'], ...driversData.props[3]['projectionValues']];
    let wodPerAoDriversRow = [...wodPerAoDriversHeader, ...wodPerAoDriversData];
    let wodPerAoDriversRowTotal = [...pastActualsMonthlyAggregation['drivers'][3]['val'], ...pastForecastMonthlyAggregation['drivers'][3]['val'], ...projectedMonthlyAggregation['drivers'][3]['val']];

    // Drivers - T-Put
    let tPutDriversHeader = [driversData.props[4]['rowData']];
    let tPutDriversData = [...driversData.props[4]['monthlyValues'], ...driversData.props[4]['weeklyValues'], ...driversData.props[4]['forecastValues'], ...driversData.props[4]['projectionValues']];
    let tPutDriversRow = [...tPutDriversHeader, ...tPutDriversData];
    let tPutDriversRowTotal = [...pastActualsMonthlyAggregation['drivers'][4]['val'], ...pastForecastMonthlyAggregation['drivers'][4]['val'], ...projectedMonthlyAggregation['drivers'][4]['val']];

    // Drivers - Pilot Tertiary L7D-Sum
    let pilotL7dSumDriversHeader = [driversData.props[5]['rowData']];
    let pilotL7dSumDriversData = [...driversData.props[5]['monthlyValues'], ...driversData.props[5]['weeklyValues'], ...driversData.props[5]['forecastValues'], ...driversData.props[5]['projectionValues']];
    let pilotL7dSumDriversRow = [...pilotL7dSumDriversHeader, ...pilotL7dSumDriversData];
    let pilotL7dSumDriversRowTotal = [...pastActualsMonthlyAggregation['drivers'][5]['val'], ...pastForecastMonthlyAggregation['drivers'][5]['val'], ...projectedMonthlyAggregation['drivers'][5]['val']];

    // Drivers - Pilot Tertiary L7D-Avg
    let pilotL7dAvgDriversHeader = [driversData.props[6]['rowData']];
    let pilotL7dAvgDriversData = [...driversData.props[6]['monthlyValues'], ...driversData.props[6]['weeklyValues'], ...driversData.props[6]['forecastValues'], ...driversData.props[6]['projectionValues']];
    let pilotL7dAvgDriversRow = [...pilotL7dAvgDriversHeader, ...pilotL7dAvgDriversData];
    let pilotL7dAvgDriversRowTotal = [...pastActualsMonthlyAggregation['drivers'][6]['val'], ...pastForecastMonthlyAggregation['drivers'][6]['val'], ...projectedMonthlyAggregation['drivers'][6]['val']];

    // Drivers - National L7- DRR
    let nationalL7DrrDriversHeader = [driversData.props[7]['rowData']];
    let nationalL7DrrDriversData = [...driversData.props[7]['monthlyValues'], ...driversData.props[7]['weeklyValues'], ...driversData.props[7]['forecastValues'], ...driversData.props[7]['projectionValues']];
    let nationalL7DrrDriversRow = [...nationalL7DrrDriversHeader, ...nationalL7DrrDriversData];
    let nationalL7DrrDriversRowTotal = [...pastActualsMonthlyAggregation['drivers'][7]['val'], ...pastForecastMonthlyAggregation['drivers'][7]['val'], ...projectedMonthlyAggregation['drivers'][7]['val']];

    // driversRow = [stockPerOutletDriversRow, aoPerSoDriversRow, thruputDriversRow, wodPerAoDriversRow, tPutDriversRow, pilotL7dSumDriversRow, pilotL7dAvgDriversRow, nationalL7DrrDriversRow];

    // Exposure
    // let exposureRow = [];

    // Exposure - Exposure
    let exposureRowHeader = [exposureData.props[0]['rowData']];
    let exposureRowData = [...exposureData.props[0]['monthlyValues'], ...exposureData.props[0]['weeklyValues'], ...exposureData.props[0]['forecastValues'], ...exposureData.props[0]['projectionValues']];
    let exposure = [...exposureRowHeader, ...exposureRowData];
    let exposureTotal = [...pastActualsMonthlyAggregation['exposure'][0]['val'], ...pastForecastMonthlyAggregation['exposure'][0]['val'], ...projectedMonthlyAggregation['exposure'][0]['val']];

    // Exposure - Exposure DOS
    let exposureDosRowHeader = [exposureData.props[1]['rowData']];
    let exposureDosRowData = [...exposureData.props[1]['monthlyValues'], ...exposureData.props[1]['weeklyValues'], ...exposureData.props[1]['forecastValues'], ...exposureData.props[1]['projectionValues']];
    let exposureDosRow = [...exposureDosRowHeader, ...exposureDosRowData];
    let exposureDosRowTotal = [...pastActualsMonthlyAggregation['exposure'][1]['val'], ...pastForecastMonthlyAggregation['exposure'][1]['val'], ...projectedMonthlyAggregation['exposure'][1]['val']];

    // exposureRow = [exposure, exposureDosRow];

    // Input
    // let inputRow = [];

    // Input - DBR DOS
    let dbrDosRowHeader = [inputsData.props[0]['rowData']];
    let dbrDosRowData = [...inputsData.props[0]['monthlyValues'], ...inputsData.props[0]['weeklyValues'], ...inputsData.props[0]['forecastValues'], ...inputsData.props[0]['projectionValues']];
    let dbrDosRow = [...dbrDosRowHeader, ...dbrDosRowData];
    let dbrDosRowTotal = [...pastActualsMonthlyAggregation['input'][0]['val'], ...pastForecastMonthlyAggregation['input'][0]['val'], ...projectedMonthlyAggregation['input'][0]['val']];

    // Input - Ret DOS
    let retDosRowHeader = [inputsData.props[1]['rowData']];
    let retDosRowData = [...inputsData.props[1]['monthlyValues'], ...inputsData.props[1]['weeklyValues'], ...inputsData.props[1]['forecastValues'], ...inputsData.props[1]['projectionValues']];
    let retDosRow = [...retDosRowHeader, ...retDosRowData];
    let retDosRowTotal = [...pastActualsMonthlyAggregation['input'][1]['val'], ...pastForecastMonthlyAggregation['input'][1]['val'], ...projectedMonthlyAggregation['input'][1]['val']];

    // Input - WOD
    let wodRowHeader = [inputsData.props[2]['rowData']];
    let wodRowData = [...inputsData.props[2]['monthlyValues'], ...inputsData.props[2]['weeklyValues'], ...inputsData.props[2]['forecastValues'], ...inputsData.props[2]['projectionValues']];
    let wodRow = [...wodRowHeader, ...wodRowData];
    let wodRowTotal = [...pastActualsMonthlyAggregation['input'][2]['val'], ...pastForecastMonthlyAggregation['input'][2]['val'], ...projectedMonthlyAggregation['input'][2]['val']];

    // Input - PLC Factor
    let plcFactorRowHeader = [inputsData.props[3]['rowData']];
    let plcFactorRowData = [...inputsData.props[3]['monthlyValues'], ...inputsData.props[3]['weeklyValues'], ...inputsData.props[3]['forecastValues'], ...inputsData.props[3]['projectionValues']];
    let plcFactorRow = [...plcFactorRowHeader, ...plcFactorRowData];
    let plcFactorRowTotal = [...pastActualsMonthlyAggregation['input'][3]['val'], ...pastForecastMonthlyAggregation['input'][3]['val'], ...projectedMonthlyAggregation['input'][3]['val']];

    // Input - Seasonality
    let seasonalityRowHeader = [inputsData.props[4]['rowData']];
    let seasonalityRowData = [...inputsData.props[4]['monthlyValues'], ...inputsData.props[4]['weeklyValues'], ...inputsData.props[4]['forecastValues'], ...inputsData.props[4]['projectionValues']];
    let seasonalityRow = [...seasonalityRowHeader, ...seasonalityRowData];
    let seasonalityRowTotal = [...pastActualsMonthlyAggregation['input'][4]['val'], ...pastForecastMonthlyAggregation['input'][4]['val'], ...projectedMonthlyAggregation['input'][4]['val']];

    // Input - Pilot Weightage
    let pilotWeightageRowHeader = [inputsData.props[5]['rowData']];
    let pilotWeightageRowData = [...inputsData.props[5]['monthlyValues'], ...inputsData.props[5]['weeklyValues'], ...inputsData.props[5]['forecastValues'], ...inputsData.props[5]['projectionValues']];
    let pilotWeightageRow = [...pilotWeightageRowHeader, ...pilotWeightageRowData];
    let pilotWeightageRowTotal = [...pastActualsMonthlyAggregation['input'][5]['val'], ...pastForecastMonthlyAggregation['input'][5]['val'], ...projectedMonthlyAggregation['input'][5]['val']];

    // Input - National Weightage
    let nationalWeightageRowHeader = [inputsData.props[6]['rowData']];
    let nationalWeightageRowData = [...inputsData.props[6]['monthlyValues'], ...inputsData.props[6]['weeklyValues'], ...inputsData.props[6]['forecastValues'], ...inputsData.props[6]['projectionValues']];
    let nationalWeightageRow = [...nationalWeightageRowHeader, ...nationalWeightageRowData];
    let nationalWeightageRowTotal = [...pastActualsMonthlyAggregation['input'][6]['val'], ...pastForecastMonthlyAggregation['input'][6]['val'], ...projectedMonthlyAggregation['input'][6]['val']];

    // Input - Parameters Weightage
    let parametersWeightageRowHeader = [inputsData.props[7]['rowData']];
    let parametersWeightageRowData = [...inputsData.props[7]['monthlyValues'], ...inputsData.props[7]['weeklyValues'], ...inputsData.props[7]['forecastValues'], ...inputsData.props[7]['projectionValues']];
    let parametersWeightageRow = [...parametersWeightageRowHeader, ...parametersWeightageRowData];
    let parametersWeightageRowTotal = [...pastActualsMonthlyAggregation['input'][7]['val'], ...pastForecastMonthlyAggregation['input'][7]['val'], ...projectedMonthlyAggregation['input'][7]['val']];

    // Input - Stocking Outlet
    let stockingOutletRowHeader = [inputsData.props[8]['rowData']];
    let stockingOutletRowData = [...inputsData.props[8]['monthlyValues'], ...inputsData.props[8]['weeklyValues'], ...inputsData.props[8]['forecastValues'], ...inputsData.props[8]['projectionValues']];
    let stockingOutletRow = [...stockingOutletRowHeader, ...stockingOutletRowData];
    let stockingOutletRowTotal = [...pastActualsMonthlyAggregation['input'][8]['val'], ...pastForecastMonthlyAggregation['input'][8]['val'], ...projectedMonthlyAggregation['input'][8]['val']];

    // Input - Activating Outlet
    let activatingOutletRowHeader = [inputsData.props[9]['rowData']];
    let activatingOutletRowData = [...inputsData.props[9]['monthlyValues'], ...inputsData.props[9]['weeklyValues'], ...inputsData.props[9]['forecastValues'], ...inputsData.props[9]['projectionValues']];
    let activatingOutletRow = [...activatingOutletRowHeader, ...activatingOutletRowData];
    let activatingOutletRowTotal = [...pastActualsMonthlyAggregation['input'][9]['val'], ...pastForecastMonthlyAggregation['input'][9]['val'], ...projectedMonthlyAggregation['input'][9]['val']];

    // inputRow = [dbrDosRow, retDosRow, wodRow, plcFactorRow, seasonalityRow, pilotWeightageRow, nationalWeightageRow, parametersWeightageRow, stockingOutletRow, activatingOutletRow];

    // Total Merge
    let finalTerVolRow = insertByKeys(terVolRow, totalKeyArr, terVolRowTotal)
    let finalsecVolRow = insertByKeys(secVolRow, totalKeyArr, secVolRowTotal)
    let finalprimVolRow = insertByKeys(primVolRow, totalKeyArr, primVolRowTotal)

    let finalOpeningDBRStockRowTotal = insertByKeys(openingDBRStockRow, totalKeyArr, openingDBRStockRowTotal)
    let finalOpeningRetailerStockRowTotal = insertByKeys(openingRetailerStockRow, totalKeyArr, openingRetailerStockRowTotal)
    let finalOpeningMarketStockRowTotal = insertByKeys(openingMarketStockRow, totalKeyArr, openingMarketStockRowTotal)
    let finalClosingDBRStockRowTotal = insertByKeys(closingDBRStockRow, totalKeyArr, closingDBRStockRowTotal)
    let finalClosingRetailerStockRowTotal = insertByKeys(closingRetailerStockRow, totalKeyArr, closingRetailerStockRowTotal)
    let finalClosingMarketStockRowTotal = insertByKeys(closingMarketStockRow, totalKeyArr, closingMarketStockRowTotal)
    let finalMarketDOSRowTotal = insertByKeys(marketDOSRow, totalKeyArr, marketDOSRowTotal)

    let finalParameterForecastTertiaryRowTotal = insertByKeys(parameterForecastTertiaryRow, totalKeyArr, parameterForecastTertiaryRowTotal)
    let finalPilotForecastTertiaryRowTotal = insertByKeys(pilotForecastTertiaryRow, totalKeyArr, pilotForecastTertiaryRowTotal)
    let finalNationalDrrForecastTertiaryRowTotal = insertByKeys(nationalDrrForecastTertiaryRow, totalKeyArr, nationalDrrForecastTertiaryRowTotal)
    let finalAoForecastTertiaryRowTotal = insertByKeys(aoForecastTertiaryRow, totalKeyArr, aoForecastTertiaryRowTotal)

    let finalStockPerOutletDriversRowTotal = insertByKeys(stockPerOutletDriversRow, totalKeyArr, stockPerOutletDriversRowTotal)
    let finalAoPerSoDriversRowTotal = insertByKeys(aoPerSoDriversRow, totalKeyArr, aoPerSoDriversRowTotal)
    let finalThruputDriversRowTotal = insertByKeys(thruputDriversRow, totalKeyArr, thruputDriversRowTotal)
    let finalWodPerAoDriversRowTotal = insertByKeys(wodPerAoDriversRow, totalKeyArr, wodPerAoDriversRowTotal)
    let finalTPutDriversRowTotal = insertByKeys(tPutDriversRow, totalKeyArr, tPutDriversRowTotal)
    let finalPilotL7dSumDriversRowTotal = insertByKeys(pilotL7dSumDriversRow, totalKeyArr, pilotL7dSumDriversRowTotal)
    let finalPilotL7dAvgDriversRowTotal = insertByKeys(pilotL7dAvgDriversRow, totalKeyArr, pilotL7dAvgDriversRowTotal)
    let finalNationalL7DrrDriversRowTotal = insertByKeys(nationalL7DrrDriversRow, totalKeyArr, nationalL7DrrDriversRowTotal)

    let finalExposureTotal = insertByKeys(exposure, totalKeyArr, exposureTotal)
    let finalExposureDosRowTotal = insertByKeys(exposureDosRow, totalKeyArr, exposureDosRowTotal)

    let finalDbrDosRowTotal = insertByKeys(dbrDosRow, totalKeyArr, dbrDosRowTotal)
    let finalRetDosRowTotal = insertByKeys(retDosRow, totalKeyArr, retDosRowTotal)
    let finalWodRowTotal = insertByKeys(wodRow, totalKeyArr, wodRowTotal)
    let finalPlcFactorRowTotal = insertByKeys(plcFactorRow, totalKeyArr, plcFactorRowTotal)
    let finalSeasonalityRowTotal = insertByKeys(seasonalityRow, totalKeyArr, seasonalityRowTotal)
    let finalPilotWeightageRowTotal = insertByKeys(pilotWeightageRow, totalKeyArr, pilotWeightageRowTotal)
    let finalNationalWeightageRowTotal = insertByKeys(nationalWeightageRow, totalKeyArr, nationalWeightageRowTotal)
    let finalParametersWeightageRowTotal = insertByKeys(parametersWeightageRow, totalKeyArr, parametersWeightageRowTotal)
    let finalStockingOutletRowTotal = insertByKeys(stockingOutletRow, totalKeyArr, stockingOutletRowTotal)
    let finalActivatingOutletRowTotal = insertByKeys(activatingOutletRow, totalKeyArr, activatingOutletRowTotal)

    let stockRow = [finalOpeningDBRStockRowTotal, finalOpeningRetailerStockRowTotal, finalOpeningMarketStockRowTotal, finalClosingDBRStockRowTotal, finalClosingRetailerStockRowTotal, finalClosingMarketStockRowTotal, finalMarketDOSRowTotal];
    let forecastTertiaryRow = [finalParameterForecastTertiaryRowTotal, finalPilotForecastTertiaryRowTotal, finalNationalDrrForecastTertiaryRowTotal, finalAoForecastTertiaryRowTotal];
    let driversRow = [finalStockPerOutletDriversRowTotal, finalAoPerSoDriversRowTotal, finalThruputDriversRowTotal, finalWodPerAoDriversRowTotal, finalTPutDriversRowTotal, finalPilotL7dSumDriversRowTotal, finalPilotL7dAvgDriversRowTotal, finalNationalL7DrrDriversRowTotal];
    let exposureRow = [finalExposureTotal, finalExposureDosRowTotal];
    let inputRow = [finalDbrDosRowTotal, finalRetDosRowTotal, finalWodRowTotal, finalPlcFactorRowTotal, finalSeasonalityRowTotal, finalPilotWeightageRowTotal, finalNationalWeightageRowTotal, finalParametersWeightageRowTotal, finalStockingOutletRowTotal, finalActivatingOutletRowTotal];

    // Convert to sheet
    let wsData = [headerRow, wkHeaderRow, finalTerVolRow, finalsecVolRow, finalprimVolRow, ...stockRow, ...forecastTertiaryRow, ...driversRow, ...exposureRow, ...inputRow];

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // 4) Build merges
    ws["!merges"] = [];
    colStart = 0;

    headerData.forEach(([title, span = 1]) => {
      if (span <= 0) return;

      const colEnd = colStart + span - 1;

      ws["!merges"].push({
        s: { r: 0, c: colStart },
        e: { r: 0, c: colEnd }
      });

      colStart = colEnd + 1;
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Forecast");
    XLSX.writeFile(wb, fileName);
  };

  useImperativeHandle(ref, () => ({
    downloadExcel: (selectedItemName, weeklyRanges) => handleDownload(selectedItemName, weeklyRanges)
  }));

  return null;
});

function insertByKeys(baseArr, keyArr, valuesArr) {
  const result = [...baseArr];

  keyArr.forEach((key, i) => {
    const insertIndex = key + i;
    result.splice(insertIndex, 0, valuesArr[i]);
  });

  return result;
}

export default DownloadExcel;
